/**
 * Distance calculation utilities using Google Maps Directions API
 */

/**
 * Calculate route distance between two points using Google Maps Directions API
 * @param {Object} origin - Origin coordinates {latitude, longitude}
 * @param {Object} destination - Destination coordinates {latitude, longitude}
 * @returns {Promise<Object>} - Promise resolving to {distance: number, duration: number, status: string}
 */
export const calculateRouteDistance = async (origin, destination) => {
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps API not loaded');
  }

  if (!origin?.latitude || !origin?.longitude || !destination?.latitude || !destination?.longitude) {
    throw new Error('Invalid origin or destination coordinates');
  }

  return new Promise((resolve, reject) => {
    const directionsService = new window.google.maps.DirectionsService();
    
    directionsService.route(
      {
        origin: { lat: origin.latitude, lng: origin.longitude },
        destination: { lat: destination.latitude, lng: destination.longitude },
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC
      },
      (result, status) => {
        if (status === 'OK') {
          const route = result.routes[0];
          const leg = route.legs[0];
          
          resolve({
            distance: leg.distance.value / 1000, // Convert to kilometers
            duration: leg.duration.value / 60, // Convert to minutes
            status: 'OK',
            distanceText: leg.distance.text,
            durationText: leg.duration.text
          });
        } else {
          reject(new Error(`Route calculation failed: ${status}`));
        }
      }
    );
  });
};

/**
 * Calculate total service distance including all route segments
 * @param {Object} locations - Object containing all location coordinates
 * @param {Object} locations.pickup - Pickup location coordinates
 * @param {Object} locations.drop - Drop location coordinates (optional)
 * @param {Object} locations.fuel - Fuel location coordinates (optional)
 * @param {Object} locations.vendor - Vendor location coordinates (optional)
 * @returns {Promise<Object>} - Promise resolving to route calculation results
 */
export const calculateServiceDistance = async (locations) => {
  const { pickup, drop, fuel, vendor } = locations;
  
  if (!pickup?.latitude || !pickup?.longitude) {
    throw new Error('Pickup location is required');
  }

  const isTowingService = drop && (
    drop.latitude !== pickup.latitude || 
    drop.longitude !== pickup.longitude ||
    drop.address !== pickup.address
  );
  
  const hasFuelLocation = fuel?.latitude && fuel?.longitude;
  const hasVendorLocation = vendor?.latitude && vendor?.longitude;

  let totalDistance = 0;
  let totalDuration = 0;
  const routeSegments = [];

  try {
    if (hasVendorLocation) {
      // Calculate vendor to pickup distance
      const vendorToPickup = await calculateRouteDistance(vendor, pickup);
      totalDistance += vendorToPickup.distance;
      totalDuration += vendorToPickup.duration;
      routeSegments.push({
        name: 'vendor_to_pickup',
        distance: vendorToPickup.distance,
        duration: vendorToPickup.duration,
        from: 'vendor',
        to: 'pickup'
      });
    }

    if (hasFuelLocation) {
      if (hasVendorLocation) {
        // Vendor -> Fuel -> Pickup
        const fuelToPickup = await calculateRouteDistance(fuel, pickup);
        totalDistance += fuelToPickup.distance;
        totalDuration += fuelToPickup.duration;
        routeSegments.push({
          name: 'fuel_to_pickup',
          distance: fuelToPickup.distance,
          duration: fuelToPickup.duration,
          from: 'fuel',
          to: 'pickup'
        });
      } else {
        // Fuel -> Pickup (no vendor location)
        const fuelToPickup = await calculateRouteDistance(fuel, pickup);
        totalDistance += fuelToPickup.distance;
        totalDuration += fuelToPickup.duration;
        routeSegments.push({
          name: 'fuel_to_pickup',
          distance: fuelToPickup.distance,
          duration: fuelToPickup.duration,
          from: 'fuel',
          to: 'pickup'
        });
      }
    }

    if (isTowingService) {
      // Calculate pickup to drop distance for towing services
      const pickupToDrop = await calculateRouteDistance(pickup, drop);
      totalDistance += pickupToDrop.distance;
      totalDuration += pickupToDrop.duration;
      routeSegments.push({
        name: 'pickup_to_drop',
        distance: pickupToDrop.distance,
        duration: pickupToDrop.duration,
        from: 'pickup',
        to: 'drop'
      });
    }

    if (hasVendorLocation) {
      // Calculate return distance
      const returnFrom = isTowingService ? drop : pickup;
      const returnToVendor = await calculateRouteDistance(returnFrom, vendor);
      totalDistance += returnToVendor.distance;
      totalDuration += returnToVendor.duration;
      routeSegments.push({
        name: 'return_to_vendor',
        distance: returnToVendor.distance,
        duration: returnToVendor.duration,
        from: isTowingService ? 'drop' : 'pickup',
        to: 'vendor'
      });
    }

    return {
      totalDistance: Math.round(totalDistance * 100) / 100, // Round to 2 decimal places
      totalDuration: Math.round(totalDuration * 100) / 100,
      routeSegments,
      isTowingService,
      hasFuelLocation,
      hasVendorLocation,
      status: 'OK'
    };

  } catch (error) {
    console.error('Error calculating service distance:', error);
    throw error;
  }
};

/**
 * Calculate simple pickup to drop distance (for non-towing services or when vendor location is not available)
 * @param {Object} pickup - Pickup location coordinates
 * @param {Object} drop - Drop location coordinates (optional, defaults to pickup)
 * @returns {Promise<Object>} - Promise resolving to distance calculation results
 */
export const calculatePickupToDropDistance = async (pickup, drop = null) => {
  if (!pickup?.latitude || !pickup?.longitude) {
    throw new Error('Pickup location is required');
  }

  // If no drop location or drop location is same as pickup, return 0
  if (!drop || 
      (drop.latitude === pickup.latitude && drop.longitude === pickup.longitude) ||
      (drop.address === pickup.address)) {
    return {
      distance: 0,
      duration: 0,
      isTowingService: false,
      status: 'OK'
    };
  }

  try {
    const result = await calculateRouteDistance(pickup, drop);
    return {
      distance: Math.round(result.distance * 100) / 100,
      duration: Math.round(result.duration * 100) / 100,
      isTowingService: true,
      status: 'OK'
    };
  } catch (error) {
    console.error('Error calculating pickup to drop distance:', error);
    throw error;
  }
};

/**
 * Check if Google Maps API is loaded
 * @returns {boolean} - True if Google Maps API is available
 */
export const isGoogleMapsLoaded = () => {
  return !!(window.google && window.google.maps && window.google.maps.DirectionsService);
}; 